import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

/**
 * RouteProgressBar · YouTube/Stripe-style top progress bar that fires on
 * route changes and Suspense boundaries. Uses location pathname to detect
 * lazy chunk loads and shows a thin mint sliver across the top of the
 * viewport for ~600ms minimum so even instant transitions feel intentional.
 *
 * Render once near the top of the dashboard layout. Sits above all content
 * (z-[100]) and ignores pointer events.
 */
export function RouteProgressBar() {
  const location = useLocation();
  const [active, setActive] = useState(false);
  const [progress, setProgress] = useState(0);
  const lastPath = useRef(location.pathname);
  const fadeTimer = useRef<number | null>(null);

  // Note: useNavigation only exists in react-router data-router setups; this
  // app uses BrowserRouter so we rely on pathname-change detection.

  useEffect(() => {
    if (location.pathname === lastPath.current) return;
    lastPath.current = location.pathname;

    if (fadeTimer.current) window.clearTimeout(fadeTimer.current);
    setActive(true);
    setProgress(8);

    const t1 = window.setTimeout(() => setProgress(45), 60);
    const t2 = window.setTimeout(() => setProgress(78), 220);
    const t3 = window.setTimeout(() => setProgress(95), 480);
    const t4 = window.setTimeout(() => setProgress(100), 700);
    fadeTimer.current = window.setTimeout(() => {
      setActive(false);
      setProgress(0);
    }, 950);

    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.clearTimeout(t3);
      window.clearTimeout(t4);
    };
  }, [location.pathname]);

  return (
    <AnimatePresence>
      {active ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          className="fixed top-0 left-0 right-0 h-[2px] z-[100] pointer-events-none"
          aria-hidden="true"
        >
          <motion.div
            className="h-full"
            style={{
              background:
                "linear-gradient(90deg, transparent, hsl(var(--dash-mint)) 30%, hsl(var(--dash-mint) / 0.85) 60%, hsl(var(--dash-mint)) 80%, transparent)",
              boxShadow: "0 0 12px rgba(0,230,118,0.6), 0 0 4px rgba(0,230,118,0.8)",
            }}
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.4, ease: "easeOut" }}
          />
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
